import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Typography, Skeleton } from '@mui/material';
import { USAMapBubbleChart,
    HighchartBarChart,
    CustomButtonGroup,
    USACountryChart } from '../';
import { DISTRIBUTION_CHART_OPTIONS,
    USA_CHART_OPTIONS,
    mapOptions } from './constants';
import { BREAKDOWN } from '../../../constants';

const MemoizedUSAMapBubbleChart = React.memo(USAMapBubbleChart);

function CustomDistrictBreakdown({
    hideDistrictMap= [],
    companyId= '',
    overallPhysicians= '0',
    stateOverallPhysicians= '0',
    isLoading= false,
    distributionRegion= [],
    districtBreakdownUSAMapData= [],
    stateDistributionRegion= [],
    stateBreakdownUSAMapData= [],
    stateMapSeries= [], // state map 
    title='Breakdown of Digital Physician Engagement',
    overallHeading='Overall Physicians',
    showOverallCount = false
}) {
    const [selectedBreakdown, setSelectedBreakdown] = useState(BREAKDOWN[0]);

    const handleDistrictBreakdown = (option) => setSelectedBreakdown(option);

    const { chartSeries, breakdown } = useMemo(() => {
        const showState = companyId && hideDistrictMap.includes(companyId) || selectedBreakdown === BREAKDOWN[1];
        return {
            chartSeries: showState ? stateDistributionRegion : distributionRegion,
            breakdown: showState ? 'State' : 'District'
        };
    }, [companyId, selectedBreakdown, hideDistrictMap, distributionRegion, stateDistributionRegion]);

    const showStateMap = companyId && hideDistrictMap.includes(companyId);
    const mapData = showStateMap ? stateBreakdownUSAMapData : districtBreakdownUSAMapData; // this is for bubble chart

    const chartHeight = useMemo(() => {
        const region = showStateMap || selectedBreakdown === BREAKDOWN[1]
            ? stateDistributionRegion
            : distributionRegion;
        return region.length > 0 ? region.length * 35 : 0;
    }, [selectedBreakdown, showStateMap, distributionRegion, stateDistributionRegion]);
    return (
        <Box className="box-sizing-border-box padding-24px">
            <Box className="padding-tb-20 border-bottom-gray flex-row-space-between-center">
                <Typography className="text-lg-semi-bold gray-900">
                    {title}
                </Typography>
                {
                    !hideDistrictMap.includes(companyId) && (
                        <CustomButtonGroup
                            data={BREAKDOWN}
                            handleClick={handleDistrictBreakdown}
                            key="breakdown"
                            selected={selectedBreakdown}
                        />
                    )
                }
            </Box>

            <Grid container className="flex-row-center">
                <Grid item className="us-bubble-map-container" lg={7} xl={8}>
                    {isLoading ? (
                        <Skeleton height={300} variant="rectangular" width="95%" />
                    ) : (
                        selectedBreakdown === BREAKDOWN[0] && !showStateMap ? (
                            <MemoizedUSAMapBubbleChart
                                chartOptions={mapOptions}
                                seriesData={mapData}
                            />
                        ) : (
                            <USACountryChart
                                chartOptions={USA_CHART_OPTIONS}
                                mapSeries={stateMapSeries}
                            />
                        )
                    )}
                </Grid>

                <Grid item lg={5} xl={4}>
                    <Box className="container-p-24-br-16 bg-gray-50 mt-24px">
                        <Box className="flex-column gap-4">
                            <Typography className="text-lg-semi-bold gray-900">
                                {title}
                            </Typography>
                            {showOverallCount && <Typography className="text-sm-regular gray-500">
                                {isLoading
                                    ? <Skeleton variant="text" width={100} />
                                    : showStateMap
                                        ? stateOverallPhysicians
                                        : overallPhysicians
                                } {overallHeading}
                            </Typography>}
                        </Box>

                        <Box className="pt-24px district-breakdown-bar-chart">
                            {isLoading ? (
                                <Skeleton height={300} variant="rectangular" width="100%" />
                            ) : (
                                <HighchartBarChart
                                    chartOptions={{
                                        ...DISTRIBUTION_CHART_OPTIONS,
                                        chart: {
                                            ...DISTRIBUTION_CHART_OPTIONS.chart,
                                            height: chartHeight
                                        }
                                    }}
                                    key={`chart-${selectedBreakdown}-${companyId}`}
                                    seriesData={chartSeries}
                                    seriesName="Digital Engagement"
                                />
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

CustomDistrictBreakdown.propTypes = {
    hideDistrictMap: PropTypes.arrayOf(PropTypes.string).isRequired,
    companyId: PropTypes.string.isRequired,
    overallPhysicians: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stateOverallPhysicians: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isLoading: PropTypes.bool.isRequired,
    distributionRegion: PropTypes.array.isRequired,
    districtBreakdownUSAMapData: PropTypes.array.isRequired,
    stateDistributionRegion: PropTypes.array.isRequired,
    stateBreakdownUSAMapData: PropTypes.array.isRequired,
    stateMapSeries: PropTypes.array.isRequired,
    title: PropTypes.string.isRequired,
    overallHeading: PropTypes.string.isRequired,
    showOverallCount: PropTypes.bool.isRequired
};

export default CustomDistrictBreakdown;
